import '@xyflow/react/dist/base.css';
import './style.css';

import {
  Background,
  BackgroundVariant,
  Controls,
  type Edge as Edge_,
  type Node as Node_,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import { memo } from 'react';
import { useEffect } from 'react';

import { Save } from '@/components/core';
import { Edge, Node } from '@/components/flow';
import { useAgentTree, useNodesAndEdges } from '@/hooks';
import { toAgentId, useInvalidateAgentQueries } from '@/hooks/api';
import { Id } from '@/utils';

const merge = <T extends Id>(lhs: T[], rhs: T[]): T[] => {
  const ids = new Set([...rhs.map((v) => v.id)]);

  return Array.from(ids).map((id) => {
    const a = lhs.find((v) => v.id === id);
    const b = rhs.find((v) => v.id === id);
    return { ...(a ?? {}), ...(b ?? {}) } as T;
  });
};

export type FlowDiagramProps = {
  agentId: string;
};

const IFlowDiagram = ({ agentId }: FlowDiagramProps) => {
  const agentTree = useAgentTree({ agentId });
  const { nodes: initialNodes, edges: initialEdges } = useNodesAndEdges(agentTree);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node_>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge_>([]);

  const { invalidate: invalidateAgent } = useInvalidateAgentQueries();

  useEffect(() => setNodes((nodes) => merge(nodes, initialNodes)), [initialNodes, setNodes]);
  useEffect(() => setEdges((edges) => merge(edges, initialEdges)), [initialEdges, setEdges]);

  useEffect(() => {
    return () => {
      // In case we navigate away, e.g. when setting up a new connection (Excel, etc)
      invalidateAgent(toAgentId(agentId));
    };
  }, [agentId, invalidateAgent]);

  if (agentTree.isPending) return <Save message="Setting up agent builder ..." />;

  return (
    <div className="h-full w-full bg-gray-50">
      <ReactFlow
        colorMode="light"
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={{ node: Node }}
        edgeTypes={{ edge: Edge }}
        fitView
        fitViewOptions={{ maxZoom: 1 }}
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#CCCCCC" />
        <Controls className="rounded-sm border border-gray-200 bg-white text-gray-600" />
      </ReactFlow>
    </div>
  );
};

export const FlowDiagram = memo(IFlowDiagram) as typeof IFlowDiagram;
